'use client';

import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import type { Properties } from 'posthog-js';
import type { AnalyticsEventName } from './events';

/**
 * Stable analytics facade for React components.
 *
 * Every method is a guarded pass-through to the PostHog client resolved from
 * context. When no provider is mounted (or the SDK never initialised)
 * `usePostHog()` yields a nullish value and all methods become safe no-ops.
 * Nothing here throws — a failing analytics call must never reach the user.
 */
export interface Analytics {
  capture: (
    event: AnalyticsEventName,
    props?: Record<string, unknown>,
    options?: { send_instantly?: boolean },
  ) => void;
  identify: (distinctId: string, props?: Record<string, unknown>) => void;
  group: (type: string, key: string, props?: Record<string, unknown>) => void;
  reset: () => void;
  register: (props: Record<string, unknown>) => void;
}

export function useAnalytics(): Analytics {
  // Typed as always-defined by posthog-js, but is genuinely undefined with no
  // provider mounted — treat it as nullable.
  const posthog = usePostHog() as ReturnType<typeof usePostHog> | undefined;

  return useMemo<Analytics>(() => {
    const guard = (label: string, fn: () => void): void => {
      try {
        fn();
      } catch (error) {
        console.warn(`[analytics] ${label} failed`, error);
      }
    };

    return {
      capture: (event, props, options) =>
        guard('capture', () => {
          posthog?.capture(event, props, options);
        }),
      identify: (distinctId, props) =>
        guard('identify', () => {
          posthog?.identify(distinctId, props as Properties | undefined);
        }),
      group: (type, key, props) =>
        guard('group', () => {
          posthog?.group(type, key, props as Properties | undefined);
        }),
      reset: () =>
        guard('reset', () => {
          posthog?.reset();
        }),
      register: (props) =>
        guard('register', () => {
          posthog?.register(props as Properties);
        }),
    };
  }, [posthog]);
}
